// 合成結果をビューポートのキャンバスへ描く (ズーム・パン・チェッカーボード)。

import type { ViewState } from '@/stores/uiStore'
import type { CompositeResult } from './compositor'
import { GLProgram } from '@/gl/program'
import { Quad } from '@/gl/quad'
import { setTextureFilter } from '@/gl/texture'
import { RECT_VERT } from './shaders/common'
import { VIEW_FRAG, ANTS_FRAG, QUICKMASK_FRAG } from './shaders/view'
import type { Rect } from '@/types/geometry'
import type { Texture } from '@/gl/texture'

export interface ViewColors {
  checkerA: [number, number, number]
  checkerB: [number, number, number]
  background: [number, number, number]
}

export class ViewRenderer {
  private program: GLProgram
  private ants: GLProgram
  private qm: GLProgram
  private quad: Quad

  constructor(private gl: WebGL2RenderingContext) {
    this.program = new GLProgram(gl, RECT_VERT, VIEW_FRAG, 'view')
    this.ants = new GLProgram(gl, RECT_VERT, ANTS_FRAG, 'ants')
    this.qm = new GLProgram(gl, RECT_VERT, QUICKMASK_FRAG, 'quickmask')
    this.quad = new Quad(gl)
  }

  /**
   * @param composite 合成結果
   * @param view ズーム・パン (CSS px)
   * @param dpr devicePixelRatio
   * @param cssW/cssH ビューポートの CSS サイズ
   */
  draw(composite: CompositeResult, view: ViewState, dpr: number, cssW: number, cssH: number, colors: ViewColors, selection: { texture: Texture; rect: Rect } | null = null, antsPhase = 0, quickMask: { live: { coverage: Texture; subtract: boolean } | null } | null = null): void {
    const gl = this.gl
    const W = Math.max(1, Math.round(cssW * dpr))
    const H = Math.max(1, Math.round(cssH * dpr))
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    gl.viewport(0, 0, W, H)
    gl.disable(gl.BLEND)
    gl.disable(gl.SCISSOR_TEST)
    gl.clearColor(colors.background[0], colors.background[1], colors.background[2], 1)
    gl.clear(gl.COLOR_BUFFER_BIT)

    // 文書座標 → デバイス px → クリップ (y を反転)
    const s = view.zoom * dpr
    const tx = view.pan.x * dpr
    const ty = view.pan.y * dpr
    // clipX = (s*x + tx) / W * 2 - 1, clipY = 1 - (s*y + ty) / H * 2
    const toClip = new Float32Array([
      (2 * s) / W, 0, 0,
      0, (-2 * s) / H, 0,
      (2 * tx) / W - 1, 1 - (2 * ty) / H, 1
    ])

    // 拡大は NEAREST、縮小はミップマップ (合成後に generateMipmap 済み)
    gl.bindTexture(gl.TEXTURE_2D, composite.texture.tex)
    if (view.zoom >= 2) {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    } else {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, view.zoom < 1 ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    }
    gl.bindTexture(gl.TEXTURE_2D, null)
    this.program.use()
    gl.uniform4f(this.program.loc('uRect'), 0, 0, composite.width, composite.height)
    gl.uniformMatrix3fv(this.program.loc('uToClip'), false, toClip)
    gl.uniform2f(this.program.loc('uDocSize'), composite.width, composite.height)
    gl.uniform1f(this.program.loc('uCheckerSize'), 8 * dpr)
    gl.uniform3f(this.program.loc('uCheckerA'), ...colors.checkerA)
    gl.uniform3f(this.program.loc('uCheckerB'), ...colors.checkerB)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, composite.texture.tex)
    gl.uniform1i(this.program.loc('uComposite'), 0)
    this.quad.draw()
    setTextureFilter(gl, composite.texture, false)
    void setTextureFilter

    if (quickMask) {
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
      const q = this.qm
      q.use()
      gl.uniform4f(q.loc('uRect'), 0, 0, composite.width, composite.height)
      gl.uniformMatrix3fv(q.loc('uToClip'), false, toClip)
      gl.uniform2f(q.loc('uDocSize'), composite.width, composite.height)
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, selection ? selection.texture.tex : null)
      gl.uniform1i(q.loc('uMask'), 0)
      gl.uniform1i(q.loc('uHasMask'), selection ? 1 : 0)
      const r = selection?.rect ?? { x: 0, y: 0, w: 1, h: 1 }
      gl.uniform4f(q.loc('uMaskRect'), r.x, r.y, r.w, r.h)
      gl.activeTexture(gl.TEXTURE1)
      gl.bindTexture(gl.TEXTURE_2D, quickMask.live ? quickMask.live.coverage.tex : null)
      gl.uniform1i(q.loc('uLive'), 1)
      gl.uniform1i(q.loc('uHasLive'), quickMask.live ? 1 : 0)
      gl.uniform1i(q.loc('uSubtract'), quickMask.live?.subtract ? 1 : 0)
      this.quad.draw()
      gl.disable(gl.BLEND)
    } else if (selection) {
      // マーチングアンツ: 選択マスクの輪郭をスクリーン空間の破線で描く
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
      const a = this.ants
      a.use()
      const pad = 2 / view.zoom
      gl.uniform4f(a.loc('uRect'), selection.rect.x - pad, selection.rect.y - pad, selection.rect.w + pad * 2, selection.rect.h + pad * 2)
      gl.uniformMatrix3fv(a.loc('uToClip'), false, toClip)
      gl.uniform4f(a.loc('uMaskRect'), selection.rect.x, selection.rect.y, selection.rect.w, selection.rect.h)
      gl.uniform1f(a.loc('uZoom'), view.zoom * dpr)
      gl.uniform1f(a.loc('uPhase'), antsPhase)
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, selection.texture.tex)
      gl.uniform1i(a.loc('uMask'), 0)
      this.quad.draw()
      gl.disable(gl.BLEND)
    }
  }

  dispose(): void {
    this.program.dispose()
    this.ants.dispose()
    this.qm.dispose()
    this.quad.dispose()
  }
}
